/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { format, parse } from 'date-fns';

/**
 * Nexus Imports
 */
// import { NeuAvatar } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { RootState } from '../../../redux/store';

import { logTableStart } from '../../../utils/analyticsHelpers';
import {
  // handleBlobPicUrl,
  // userProfilePicUrl,
  yearsOfService
} from '../../../utils/helpers';

/**
 * Style Imports
 */
import {
  GoBackBtn,
  GoBackContainer,
  HeaderTitle,
  UserInfo,
  ProfileContainer,
  SubToolbar
} from './EmployeeProfileHeader.styles';

/**
 * Global Type Definition Imports
 */
import { Employee } from '../../../config/interfaces';

/**
 * Static File Imports
 */
// import defaultImage from '../../../assets/images/defaultImage.png';

interface IEmployeeProfileDrawerProps {
  // sasToken: string;
  selectedEmployee?: Employee | null;
}

/**
 * Determine the validation profile route based on current pathname
 *
 * @param pathname - Current URL pathname
 * @returns Validation profile route path
 */
const getValidationListRoute = (pathname: string): string => {
  if (pathname.includes('/nlr')) return '/validation/nlr/list';
  if (pathname.includes('/aidet')) return '/validation/aidet/list';
  if (pathname.includes('/bssr')) return '/validation/bssr/list';
  return '/validation/nlr/list'; // fallback
};

const EmployeeProfileDrawer: FC<IEmployeeProfileDrawerProps> = ({
  // sasToken,
  selectedEmployee
}) => {
  const [anniversary, setAnniversary] = useState<string>('');
  const [email, setEmail] = useState<string>(selectedEmployee?.email || '');
  const [effectiveStartDate, setEffectiveStartDate] = useState<string>('');
  const [rnExperienceDate, setRnExperienceDate] = useState<string>('');

  const { pathname, state } = useLocation();
  const navigate = useNavigate();

  const { logTrackingEvent } = useAnalyticsApi();

  const { from = '' } = state ?? {};

  const profileType = useMemo(
    () =>
      pathname.includes('/employees')
        ? 'employee'
        : pathname.includes('/validation')
        ? 'validation'
        : 'support',
    [pathname]
  );

  const goback = () => {
    let returnRoute = from;

    if (profileType === 'employee') {
      returnRoute = from;
    } else if (profileType === 'validation') {
      returnRoute = getValidationListRoute(pathname);
    } else if (profileType === 'support') {
      returnRoute = pathname.includes('/csc')
        ? '/support/csc/list'
        : '/support/cned/list';
    }

    logTrackingEvent(logTableStart(returnRoute));
    navigate(-1);
  };

  useEffect(() => {
    if (selectedEmployee) {
      setEmail(selectedEmployee.email);
      if (selectedEmployee.effectiveStartDate?.includes('/')) {
        setEffectiveStartDate(selectedEmployee.effectiveStartDate);
      } else if (
        selectedEmployee.effectiveStartDate &&
        selectedEmployee.effectiveStartDate !== 'NULL' &&
        !selectedEmployee.effectiveStartDate?.includes('/')
      ) {
        setEffectiveStartDate(
          format(
            parse(selectedEmployee.effectiveStartDate, 'yyyyMMdd', new Date()),
            'MM/dd/yyyy'
          )
        );

        if (selectedEmployee.annivDate) {
          setAnniversary(selectedEmployee.annivDate);
        } else setAnniversary('N/A');
      } else if (
        selectedEmployee.startDate &&
        selectedEmployee.startDate !== 'NULL'
      ) {
        setEffectiveStartDate(selectedEmployee.startDate);
      } else setEffectiveStartDate('N/A');
      if (selectedEmployee.annivDate && selectedEmployee.annivDate !== 'NULL') {
        setAnniversary(selectedEmployee.annivDate);
      } else setAnniversary('N/A');
      if (selectedEmployee.rnExperienceDate)
        setRnExperienceDate(selectedEmployee.rnExperienceDate);
    }
  }, []);

  return (
    <SubToolbar id="employee-profile-drawer">
      <GoBackContainer
        onClick={goback}
        onKeyDown={goback}
        role="button"
        tabIndex={0}
      >
        <GoBackBtn
          color="blue"
          icon="chevron_left"
          kind="tertiary"
          size="small"
        >
          Go Back
        </GoBackBtn>
      </GoBackContainer>
      <ProfileContainer>
        {/* <NeuAvatar
          id="SubNav-Employee-User-Image"
          color="gray"
          size="small"
          style={{ '--avatar-size': '3rem' }}
        >
          <img
            src={
              sasToken && selectedEmployee
                ? handleBlobPicUrl(selectedEmployee.pictureUrl, sasToken) ??
                  userProfilePicUrl(selectedEmployee?.hcaid, sasToken)
                : defaultImage
            }
            alt="Profile"
            onError={e => {
              e.currentTarget.src = defaultImage;
            }}
            style={{
              borderRadius: '50%',
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </NeuAvatar> */}

        <HeaderTitle id="SubNav-Employee-User-Name-34">
          Name & 3-4 ID
          <UserInfo>
            {selectedEmployee?.firstName} {selectedEmployee?.lastName} |{' '}
            {selectedEmployee?.hcaid}
          </UserInfo>
        </HeaderTitle>

        <HeaderTitle id="SubNav-Employee-User-Email">
          Email
          <UserInfo>{email}</UserInfo>
        </HeaderTitle>

        <HeaderTitle id="SubNav-Employee-User-Anniversary">
          Anniversary
          <UserInfo>{anniversary}</UserInfo>
        </HeaderTitle>

        <HeaderTitle id="SubNav-Employee-User-Dept-Start">
          Dept Start Date
          <UserInfo>{effectiveStartDate}</UserInfo>
        </HeaderTitle>

        {selectedEmployee?.title !== 'LPN' && (
          <HeaderTitle id="SubNav-Employee-User-Rn-Experience">
            RN Experience
            <UserInfo>
              {rnExperienceDate ? yearsOfService(rnExperienceDate) : 'Unknown'}
            </UserInfo>
          </HeaderTitle>
        )}
      </ProfileContainer>
    </SubToolbar>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser.authorizedUser
  // sasToken: state.ConfigReducer.sasToken
});

export default connect(mapReduxStateToProps)(EmployeeProfileDrawer);
